import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, CircularProgress, Alert, TextField, Select, MenuItem } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import {
  fetchDocumentBank,
  createDocumentBankItem,
  updateDocumentBankItem,
  deleteDocumentBankItem,
} from "../../api/backend";
import { useActiveTenant } from "../../hooks/useActiveTenant";
import { useConfigList } from "../../hooks/useConfigList";
import NoTenantSelected from "../../components/NoTenantSelected";

const DEFAULT_CATEGORIES = ["Firefighter", "EMS", "Hazmat", "Search & Rescue", "Fire Officer", "General"];

const D_STATIC = {
  ink: "var(--color-ink)", ink2: "var(--color-ink-2)", ink3: "var(--color-ink-3)", ink4: "var(--color-ink-4)",
  line: "var(--color-hairline-cloud)", line2: "var(--color-surface-press-light)", surface2: "var(--color-surface-2)", surface3: "var(--color-surface-press-light)",
  danger: "#B42318", danger50: "rgba(180,35,24,.08)", danger100: "rgba(180,35,24,.18)",
  shadowSm: "0 1px 0 rgba(20,24,40,.04),0 1px 3px rgba(20,24,40,.06)",
};

function useD() {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const amber = theme.palette.warning.main;
  return {
    ...D_STATIC,
    primary,
    primary700: theme.palette.primary.dark,
    primary10: alpha(primary, 0.06),
    amber,
    amber50: alpha(amber, 0.10),
  };
}

const CONTENT_TYPES = ["LINK", "PDF", "VIDEO", "ANNOUNCEMENT"];

function SvgIcon({ size = 15, sw = 2, children }) {
  return <Box component="svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">{children}</Box>;
}

function Btn({ children, primary, danger, sm, onClick, disabled, icon }) {
  const D = useD();
  const h = sm ? 32 : 38; const px = sm ? "13px" : "18px"; const fs = sm ? "12.5px" : "13px";
  const base = { height: h, px, borderRadius: sm ? "9px" : "11px", border: 0, fontFamily: "inherit", fontSize: fs, fontWeight: 700, cursor: disabled ? "default" : "pointer", display: "inline-flex", alignItems: "center", gap: "7px", transition: "all .13s", whiteSpace: "nowrap" };
  const v = primary ? { bgcolor: disabled ? D.surface3 : D.primary, color: disabled ? D.ink3 : "#fff", boxShadow: disabled ? "none" : `0 4px 12px -4px ${alpha(D.primary, 0.45)}`, "&:hover:not(:disabled)": { bgcolor: D.primary700 } }
    : danger ? { bgcolor: D.danger50, color: D.danger, border: `1.5px solid ${D.danger100}`, "&:hover": { bgcolor: D.danger100 } }
    : { bgcolor: D.surface2, color: D.ink2, border: `1.5px solid ${D.line}`, "&:hover": { bgcolor: D.surface3 } };
  return <Box component="button" onClick={onClick} disabled={disabled} sx={{ ...base, ...v }}>{icon}{children}</Box>;
}

function Lbl({ children, opt }) {
  return (
    <Box sx={{ fontSize: "11.5px", fontWeight: 700, letterSpacing: ".07em", textTransform: "uppercase", color: D_STATIC.ink3, mb: "7px" }}>
      {children}{opt && <Box component="span" sx={{ fontWeight: 500, textTransform: "none", letterSpacing: 0, color: D_STATIC.ink4, ml: "4px" }}>— optional</Box>}
    </Box>
  );
}

function parseTags(raw = "[]") {
  try { const v = JSON.parse(raw); return Array.isArray(v) ? v : []; } catch { return raw.split(",").map(s => s.trim()).filter(Boolean); }
}

const EMPTY = { title: "", description: "", contentType: "LINK", url: "", tags: "" };

function DocForm({ initial, onSave, onCancel, saving }) {
  const D = useD();
  const cSx = { "& .MuiOutlinedInput-root": { borderRadius: "12px", bgcolor: D.surface2, "& fieldset": { borderColor: D.line, borderWidth: "1.5px" }, "&:hover fieldset": { borderColor: D.ink3 }, "&.Mui-focused fieldset": { borderColor: D.primary, borderWidth: "1.5px" }, "&.Mui-focused": { bgcolor: "#fff" } }, "& .MuiInputBase-input": { fontSize: "14px", fontWeight: 500, color: D.ink } };
  const mSx = { ...cSx, "& .MuiOutlinedInput-root": { ...cSx["& .MuiOutlinedInput-root"], height: "auto" } };
  const CATEGORIES = useConfigList("SKILL_CATEGORY", DEFAULT_CATEGORIES);
  const [form, setForm] = useState(initial || EMPTY);
  const [file, setFile] = useState(null);
  const set = k => e => setForm(p => ({ ...p, [k]: e.target.value }));
  const tagList = parseTags(typeof form.tags === "string" ? form.tags : form.tags.join(", "));
  const currentCategory = tagList.find(t => CATEGORIES.includes(t)) || "";
  const otherTags = tagList.filter(t => !CATEGORIES.includes(t));
  const setCategory = (e) => {
    const newCat = e.target.value;
    setForm(p => ({ ...p, tags: (newCat ? [newCat, ...otherTags] : otherTags).join(", ") }));
  };
  const setOtherTags = (e) => {
    const parsed = parseTags(e.target.value);
    setForm(p => ({ ...p, tags: (currentCategory ? [currentCategory, ...parsed] : parsed).join(", ") }));
  };

  const save = () => {
    const tags = JSON.stringify(parseTags(typeof form.tags === 'string' ? form.tags : form.tags.join(', ')));
    const payload = { ...form, tags };
    if (form.contentType === 'LINK' || !file) {
      onSave(payload);
    } else {
      const fd = new FormData();
      fd.append('data', JSON.stringify(payload));
      fd.append('file', file);
      onSave(fd);
    }
  };

  const needsFile = form.contentType !== "LINK" && form.contentType !== "ANNOUNCEMENT";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", p: "20px" }}>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "14px" }}>
        <Box><Lbl>Title</Lbl><TextField fullWidth size="small" value={form.title} onChange={set("title")} sx={{ ...cSx, "& .MuiOutlinedInput-root": { ...cSx["& .MuiOutlinedInput-root"], height: 44 } }} /></Box>
        <Box>
          <Lbl>Type</Lbl>
          <Select size="small" value={form.contentType} onChange={set("contentType")} fullWidth
            sx={{ height: 44, borderRadius: "12px", bgcolor: D.surface2, "& fieldset": { borderColor: D.line, borderWidth: "1.5px" }, fontSize: "14px" }}>
            {CONTENT_TYPES.map(t => <MenuItem key={t} value={t} sx={{ fontSize: "14px" }}>{t}</MenuItem>)}
          </Select>
        </Box>
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "14px" }}>
        <Box>
          <Lbl opt>Category</Lbl>
          <Select fullWidth size="small" displayEmpty value={currentCategory} onChange={setCategory}
            sx={{ height: 44, borderRadius: "12px", bgcolor: D.surface2, "& fieldset": { borderColor: D.line, borderWidth: "1.5px" }, fontSize: "14px" }}>
            <MenuItem value=""><em>None</em></MenuItem>
            {CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </Select>
        </Box>
        <Box><Lbl opt>Tags</Lbl><TextField fullWidth size="small" placeholder="nfpa, reference" value={otherTags.join(", ")} onChange={setOtherTags} sx={{ ...cSx, "& .MuiOutlinedInput-root": { ...cSx["& .MuiOutlinedInput-root"], height: 44 } }} /></Box>
      </Box>
      <Box><Lbl opt>Description</Lbl><TextField fullWidth multiline rows={2} value={form.description || ""} onChange={set("description")} sx={mSx} /></Box>
      {form.contentType === "LINK" && (
        <Box><Lbl>URL</Lbl><TextField fullWidth size="small" placeholder="https://example.com/resource" value={form.url || ""} onChange={set("url")} sx={{ ...cSx, "& .MuiOutlinedInput-root": { ...cSx["& .MuiOutlinedInput-root"], height: 44 } }} /></Box>
      )}
      {needsFile && (
        <Box>
          <Lbl opt>File</Lbl>
          <Box component="input" type="file" onChange={e => setFile(e.target.files[0])}
            sx={{ display: "block", fontSize: "13px", color: D.ink2, cursor: "pointer", "&::file-selector-button": { mr: "12px", px: "14px", py: "6px", borderRadius: "8px", border: `1.5px solid ${D.line}`, bgcolor: D.surface2, fontSize: "12.5px", fontWeight: 700, cursor: "pointer" } }} />
          {file && <Box sx={{ fontSize: "12px", color: D.ink3, mt: "6px" }}>{file.name} ({(file.size / 1024).toFixed(1)} KB)</Box>}
        </Box>
      )}
      <Box sx={{ display: "flex", gap: "9px", justifyContent: "flex-end" }}>
        <Btn sm onClick={onCancel}>Cancel</Btn>
        <Btn primary sm onClick={save} disabled={!form.title || saving} icon={saving ? <CircularProgress size={12} sx={{ color: "inherit" }} /> : null}>
          {saving ? "Saving…" : "Save to Bank"}
        </Btn>
      </Box>
    </Box>
  );
}

export default function DocumentBankPage() {
  const D = useD();
  const { tenantId: activeTenantId, isGlobalAdmin } = useActiveTenant();
  const KIND_COLORS = { LINK: D.primary, PDF: "#B42318", VIDEO: "var(--color-accent-violet)", ANNOUNCEMENT: "#15803D" };
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [err, setErr] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteErr, setDeleteErr] = useState(null);

  const load = useCallback(() => {
    if (isGlobalAdmin && !activeTenantId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchDocumentBank(search || undefined).then(d => { setItems(Array.isArray(d) ? d : []); setLoading(false); }).catch(() => { setErr("Failed to load."); setLoading(false); });
  }, [search, activeTenantId, isGlobalAdmin]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (form) => { setSaving(true); try { const item = await createDocumentBankItem(form); setItems(p => [item, ...p]); setShowCreate(false); } catch { setErr("Failed to create."); } finally { setSaving(false); } };
  const handleUpdate = async (id, form) => { setSaving(true); try { const u = await updateDocumentBankItem(id, form); setItems(p => p.map(i => i.id === id ? u : i)); setEditId(null); } catch { setErr("Failed to update."); } finally { setSaving(false); } };
  const handleDelete = async (id) => { setDeleteErr(null); try { await deleteDocumentBankItem(id); setItems(p => p.filter(i => i.id !== id)); setDeleteId(null); } catch (e) { setDeleteErr(e?.response?.data?.message || "Cannot delete — may be in use."); } };

  if (isGlobalAdmin && !activeTenantId) {
    return (
      <Box sx={{ maxWidth: 1160, mx: "auto" }}>
        <NoTenantSelected message="Select a training center from the top bar to view its document bank." />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1160, mx: "auto" }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: "20px", flexWrap: "wrap", gap: "12px" }}>
        <Box>
          <Typography sx={{ fontSize: "26px", fontWeight: 800, letterSpacing: "-.03em", color: D.ink, m: 0, fontFamily: "var(--font-display)" }}>Document Bank</Typography>
          <Box sx={{ fontSize: "13.5px", color: D.ink3, mt: "4px" }}>Reusable links, files, and resources — assign to any course.</Box>
        </Box>
        <Btn primary onClick={() => { setShowCreate(true); setEditId(null); }} icon={<SvgIcon size={13} sw={2.5}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></SvgIcon>}>New Document</Btn>
      </Box>

      {err && <Alert severity="error" onClose={() => setErr(null)} sx={{ mb: 2, borderRadius: "12px" }}>{err}</Alert>}

      <Box sx={{ bgcolor: "#fff", border: `1px solid ${D.line}`, borderRadius: "12px", px: "14px", display: "flex", alignItems: "center", gap: "10px", height: 44, mb: "16px", maxWidth: 380 }}>
        <SvgIcon size={14}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></SvgIcon>
        <Box component="input" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search documents…" sx={{ flex: 1, border: 0, outline: 0, bgcolor: "transparent", fontSize: "14px", color: D.ink }} />
      </Box>

      {showCreate && (
        <Box sx={{ bgcolor: "#fff", border: `1px solid ${D.line}`, borderRadius: "16px", boxShadow: D.shadowSm, overflow: "hidden", mb: "16px" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "11px", px: "20px", py: "13px", borderBottom: `1px solid ${D.line2}` }}>
            <Box sx={{ width: 28, height: 28, borderRadius: "8px", bgcolor: D.primary10, color: D.primary, display: "grid", placeItems: "center" }}>
              <SvgIcon><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></SvgIcon>
            </Box>
            <Typography sx={{ fontSize: "15px", fontWeight: 700, color: D.ink, m: 0, fontFamily: "var(--font-display)" }}>New Document</Typography>
          </Box>
          <DocForm onSave={handleCreate} onCancel={() => setShowCreate(false)} saving={saving} />
        </Box>
      )}

      {loading ? <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress sx={{ color: D.primary }} /></Box>
        : items.length === 0 ? <Box sx={{ bgcolor: "#fff", border: `1px solid ${D.line}`, borderRadius: "16px", p: "40px", textAlign: "center", color: D.ink4, fontSize: "13.5px" }}>{search ? "No documents match." : "No documents in the bank yet."}</Box>
        : <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {items.map(item => (
            <Box key={item.id} sx={{ bgcolor: "#fff", border: `1px solid ${D.line}`, borderRadius: "16px", boxShadow: D.shadowSm, overflow: "hidden" }}>
              {editId === item.id ? (
                <>
                  <Box sx={{ px: "20px", py: "13px", borderBottom: `1px solid ${D.line2}`, display: "flex", alignItems: "center", gap: "8px" }}>
                    <Typography sx={{ fontSize: "15px", fontWeight: 700, color: D.ink, m: 0, fontFamily: "var(--font-display)" }}>Edit: {item.title}</Typography>
                  </Box>
                  <DocForm initial={{ ...item, tags: parseTags(item.tags).join(", ") }} onSave={f => handleUpdate(item.id, f)} onCancel={() => setEditId(null)} saving={saving} />
                </>
              ) : (
                <Box sx={{ p: "16px 20px" }}>
                  <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "12px" }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: "9px", flexWrap: "wrap", mb: "4px" }}>
                        <Box sx={{ fontSize: "10px", fontWeight: 800, letterSpacing: ".07em", textTransform: "uppercase", px: "7px", py: "2px", borderRadius: "6px", bgcolor: KIND_COLORS[item.contentType] + "18", color: KIND_COLORS[item.contentType] }}>{item.contentType}</Box>
                        <Box sx={{ fontSize: "15px", fontWeight: 700, color: D.ink }}>{item.title}</Box>
                        {item.isSystem && <Box component="span" sx={{ fontSize: "10px", fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", px: "8px", py: "2px", borderRadius: "999px", bgcolor: D.amber50, color: D.amber }}>System</Box>}
                      </Box>
                      {item.description && <Box sx={{ fontSize: "13px", color: D.ink3, mb: "6px" }}>{item.description}</Box>}
                      {item.url && <Box sx={{ fontSize: "12px", color: D.primary, mb: "6px", wordBreak: "break-all" }}>{item.url}</Box>}
                      {item.fileName && <Box sx={{ fontSize: "12px", color: D.ink4 }}>{item.fileName} {item.fileSize ? `(${(item.fileSize / 1024).toFixed(1)} KB)` : ""}</Box>}
                      <Box sx={{ display: "flex", gap: "6px", flexWrap: "wrap", mt: "6px" }}>
                        {parseTags(item.tags).map(t => <Box key={t} component="span" sx={{ fontSize: "11px", fontWeight: 700, px: "8px", py: "3px", borderRadius: "999px", bgcolor: D.primary10, color: D.primary }}>{t}</Box>)}
                      </Box>
                    </Box>
                    {!item.isSystem && (
                      <Box sx={{ display: "flex", gap: "6px", flexShrink: 0 }}>
                        <Box component="button" onClick={() => setEditId(item.id)} sx={{ width: 32, height: 32, borderRadius: "8px", border: `1.5px solid ${D.line}`, bgcolor: "#fff", display: "grid", placeItems: "center", cursor: "pointer", color: D.ink3, "&:hover": { bgcolor: D.surface3 } }}>
                          <SvgIcon size={13}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></SvgIcon>
                        </Box>
                        <Box component="button" onClick={() => { setDeleteId(item.id); setDeleteErr(null); }} sx={{ width: 32, height: 32, borderRadius: "8px", border: `1.5px solid ${D.line}`, bgcolor: "#fff", display: "grid", placeItems: "center", cursor: "pointer", color: D.ink3, "&:hover": { bgcolor: D.danger50, color: D.danger, borderColor: D.danger100 } }}>
                          <SvgIcon size={13}><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></SvgIcon>
                        </Box>
                      </Box>
                    )}
                  </Box>
                  {deleteId === item.id && (
                    <Box sx={{ mt: "12px", p: "12px 14px", bgcolor: D.danger50, border: `1.5px solid ${D.danger100}`, borderRadius: "10px" }}>
                      {deleteErr && <Box sx={{ fontSize: "12.5px", color: D.danger, mb: "8px" }}>{deleteErr}</Box>}
                      <Box sx={{ fontSize: "13.5px", fontWeight: 600, color: D.danger, mb: "10px" }}>Delete "{item.title}"?</Box>
                      <Box sx={{ display: "flex", gap: "8px" }}>
                        <Btn sm onClick={() => setDeleteId(null)}>Cancel</Btn>
                        <Btn danger sm onClick={() => handleDelete(item.id)}>Delete</Btn>
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          ))}
        </Box>}
    </Box>
  );
}
